// telegram.js - Thin adapter around the Telegram Mini App (WebApp) SDK.
//
// The SDK script in index.html defines window.Telegram.WebApp. Outside Telegram
// it still exists but reports platform 'unknown' with empty initData, so every
// function here degrades to a safe no-op and the game runs normally in a browser.

const WebApp =
  (typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp) || null;

// Older Telegram clients lack newer methods; never let a missing feature throw.
const safe = (fn) => {
  try {
    return fn();
  } catch (e) {
    /* feature not supported on this client version */
  }
};

export const isInTelegram = () =>
  !!(WebApp && WebApp.platform && WebApp.platform !== 'unknown');

// Bring the Mini App up: claim the full height, match our dark chrome, and stop
// the swipe-down-to-close gesture from fighting gameplay. Keeps a CSS variable
// (--tg-vh) in sync with Telegram's viewport so the layout fills it exactly.
export const initTelegram = () => {
  if (!WebApp) return;
  safe(() => WebApp.ready());
  if (!isInTelegram()) return; // in a plain browser: leave theme/viewport alone

  safe(() => WebApp.expand());
  safe(() => WebApp.setHeaderColor('#05081a'));
  safe(() => WebApp.setBackgroundColor('#05081a'));
  if (typeof WebApp.disableVerticalSwipes === 'function') {
    safe(() => WebApp.disableVerticalSwipes());
  }

  const applyViewport = () => {
    const h = WebApp.viewportHeight;
    if (h && h > 0) {
      document.documentElement.style.setProperty('--tg-vh', `${h}px`);
    }
  };
  applyViewport();
  safe(() => WebApp.onEvent('viewportChanged', applyViewport));
};

// The verified-by-Telegram user (id/username/first_name), or null in a browser.
export const getTelegramUser = () =>
  (WebApp && WebApp.initDataUnsafe && WebApp.initDataUnsafe.user) || null;

// Signed init data string — a future backend verifies its hash to trust scores.
export const getInitData = () => (WebApp && WebApp.initData) || '';

// Haptic feedback. kind: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' for an
// impact, or 'success' | 'warning' | 'error' for a notification.
export const haptic = (kind) => {
  const H = WebApp && WebApp.HapticFeedback;
  if (!H) return;
  safe(() => {
    if (kind === 'success' || kind === 'warning' || kind === 'error') {
      H.notificationOccurred(kind);
    } else {
      H.impactOccurred(kind);
    }
  });
};
